import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import Login from './components/Login.jsx';
import Dashboard from './components/Dashboard.jsx';
import EventManagement from './components/EventManagement.jsx';
import EventDetails from './components/EventDetails.jsx';
import TischVorlagen from './components/TischVorlagen.jsx';
import CheckInInterface from './components/CheckInInterface.jsx';
import PublicView from './components/PublicView.jsx';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Setup axios defaults
axios.defaults.baseURL = API;
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Axios error:', error.response?.status, error.response?.data);
    
    // Only redirect on certain 401 errors, not all
    if (error.response?.status === 401 && error.response?.data?.detail?.includes('credentials')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token, isHostess = false) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    toast.success('Erfolgreich angemeldet!');
    
    // Redirect hostess directly to events
    if (isHostess || userData.role === 'hostess') {
      window.location.href = '/events';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Erfolgreich abgemeldet!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Routes (no authentication required) */}
          <Route path="/public/event/:token" element={<PublicView />} />
          
          {!user ? (
            <>
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          ) : (
            <>
              <Route 
                path="/" 
                element={
                  user.role === 'hostess' 
                    ? <Navigate to="/events" replace /> 
                    : <Dashboard user={user} onLogout={handleLogout} />
                } 
              />
              <Route path="/events" element={<EventManagement user={user} onLogout={handleLogout} />} />
              <Route path="/events/:eventId" element={<EventDetails user={user} onLogout={handleLogout} />} />
              <Route path="/events/:eventId/checkin" element={<CheckInInterface user={user} onLogout={handleLogout} />} />
              <Route path="/tischvorlagen" element={<TischVorlagen user={user} onLogout={handleLogout} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;